/**
 * Medição, remarketing e desafio anti-robô das landing pages.
 *
 * ## Por que a configuração é global e a página só complementa
 *
 * O GA4 e o pixel da Meta da EGBA são os mesmos em toda landing page — é assim
 * que público de remarketing e funil se acumulam num lugar só. Cadastrar o
 * mesmo identificador em cada página produz, na melhor hipótese, o mesmo
 * resultado com N vezes mais chance de erro de digitação; na pior, uma página
 * nova sai sem medição nenhuma e ninguém percebe até o relatório do mês.
 * Por isso a medição vive em `marketing.medicao`, e a página só acrescenta o
 * que for dela (uma campanha com pixel de parceiro, por exemplo).
 *
 * ## Por que existe GTM
 *
 * "Outros funis" é uma lista que não acaba: Hotjar, Clarity, RD Station, Pinterest,
 * Kwai. Implementar cada um aqui é assinar manutenção eterna e, pior, exigir
 * deploy para uma decisão de marketing. O contêiner do Google Tag Manager é a
 * porta única: qualquer tag entra por lá, configurada por quem faz marketing,
 * sem tocar em código — e continua sujeita ao consentimento, porque o contêiner
 * inteiro só carrega depois do "aceitar".
 *
 * Não existe campo de "cole seu JavaScript aqui" de propósito: seria injeção de
 * script arbitrário numa página do domínio do Governo do Estado, e a proteção
 * que a plataforma inteira mantém sobre dado de aluno não sobrevive a um
 * `<script>` de terceiro rodando na mesma origem.
 *
 * ## Por que o captcha não espera consentimento
 *
 * Pixel é medição e depende de "aceitar". Turnstile e reCAPTCHA são proteção do
 * formulário contra abuso — tratamento necessário para a segurança do titular e
 * do serviço (LGPD, art. 7º, X e art. 10). Ficam fora da barra de cookies, e é
 * por isso que estão marcados como estritamente necessários.
 */

export type ProvedorPixel = 'ga4' | 'gtm' | 'google_ads' | 'meta' | 'tiktok' | 'linkedin'

export interface Pixel {
  provedor: ProvedorPixel
  id: string
  /**
   * Google Ads apenas: o rótulo da conversão (`AbCdEfGh`), que acompanha o
   * `AW-…` no evento. Sem ele o Ads registra visita e nunca registra lead.
   */
  rotuloConversao?: string
  /** False only for strictly necessary tags. Marketing pixels are always true. */
  requerConsentimento: boolean
}

export const ROTULOS_PIXEL: Record<ProvedorPixel, { nome: string; exemplo: string; ajuda: string }> =
  {
    ga4: {
      nome: 'Google Analytics 4',
      exemplo: 'G-XXXXXXXXXX',
      ajuda: 'Mede visitas, origem do tráfego e o evento de lead.',
    },
    gtm: {
      nome: 'Google Tag Manager',
      exemplo: 'GTM-XXXXXXX',
      ajuda: 'Contêiner para qualquer outra tag (Hotjar, Clarity, RD, Pinterest) sem deploy.',
    },
    google_ads: {
      nome: 'Google Ads',
      exemplo: 'AW-XXXXXXXXX',
      ajuda: 'Conversão e público de remarketing no Google e no YouTube.',
    },
    meta: {
      nome: 'Meta (Facebook e Instagram)',
      exemplo: '1234567890123456',
      ajuda: 'Pixel único das duas redes — é ele que alimenta o público de remarketing.',
    },
    tiktok: {
      nome: 'TikTok',
      exemplo: 'CXXXXXXXXXXXXXXXXXXX',
      ajuda: 'Pixel do TikTok Ads Manager.',
    },
    linkedin: {
      nome: 'LinkedIn',
      exemplo: '1234567',
      ajuda: 'Partner ID do Insight Tag.',
    },
  }

export const PROVEDORES_PIXEL = Object.keys(ROTULOS_PIXEL) as ProvedorPixel[]

/** A medição da conta, aplicada a todas as landing pages. */
export interface ConfigMedicao {
  ga4Id?: string
  gtmId?: string
  googleAdsId?: string
  googleAdsRotuloConversao?: string
  metaPixelId?: string
  tiktokId?: string
  linkedinId?: string
  /** Desligar suspende a medição em todas as páginas sem apagar os IDs. */
  ativo?: boolean
}

const FORMATO: Partial<Record<ProvedorPixel, RegExp>> = {
  ga4: /^G-[A-Z0-9]{4,}$/i,
  gtm: /^GTM-[A-Z0-9]{4,}$/i,
  google_ads: /^AW-\d{6,}$/i,
  meta: /^\d{8,20}$/,
  linkedin: /^\d{4,12}$/,
}

/**
 * Erro de formato, ou `null`.
 *
 * Vale a pena conferir porque um ID errado falha em silêncio: a tag carrega,
 * não reclama, e a campanha roda um mês inteiro sem medir nada.
 */
export function erroDeFormato(provedor: ProvedorPixel, id: string): string | null {
  const limpo = id.trim()
  if (!limpo) return 'Informe o identificador.'
  const re = FORMATO[provedor]
  if (re && !re.test(limpo)) {
    return `Formato esperado: ${ROTULOS_PIXEL[provedor].exemplo}`
  }
  return null
}

/** Converte a configuração da conta na mesma lista de pixels que a página usa. */
export function pixelsDaConfig(m: ConfigMedicao | null | undefined): Pixel[] {
  if (!m || m.ativo === false) return []
  const lista: Pixel[] = []
  const põe = (provedor: ProvedorPixel, id: string | undefined, rotuloConversao?: string): void => {
    const limpo = id?.trim()
    if (!limpo) return
    lista.push({
      provedor,
      id: limpo,
      ...(rotuloConversao?.trim() ? { rotuloConversao: rotuloConversao.trim() } : {}),
      requerConsentimento: true,
    })
  }
  põe('ga4', m.ga4Id)
  põe('gtm', m.gtmId)
  põe('google_ads', m.googleAdsId, m.googleAdsRotuloConversao)
  põe('meta', m.metaPixelId)
  põe('tiktok', m.tiktokId)
  põe('linkedin', m.linkedinId)
  return lista
}

/**
 * Junta a medição da conta com a da página, sem repetir.
 *
 * A página vem primeiro para vencer o empate: se alguém cadastrou o mesmo
 * provedor na página, é porque quis outro destino ali — provavelmente o pixel
 * de um parceiro numa campanha conjunta.
 */
export function mesclarPixels(daPagina: Pixel[], daConta: Pixel[]): Pixel[] {
  const saida: Pixel[] = []
  const vistos = new Set<string>()
  for (const p of [...daPagina, ...daConta]) {
    const id = p.id?.trim()
    if (!id) continue
    const chave = `${p.provedor}:${id.toLowerCase()}`
    if (vistos.has(chave)) continue
    vistos.add(chave)
    saida.push({ ...p, id })
  }
  return saida
}

// ---------------------------------------------------------------------------
// Desafio anti-robô
// ---------------------------------------------------------------------------

export type ProvedorCaptcha = 'nenhum' | 'turnstile' | 'recaptcha_v3'

export interface ConfigCaptcha {
  provedor: ProvedorCaptcha
  /** Pública por definição: vai no HTML. */
  siteKey?: string
  /**
   * Recusar o envio quando o servidor não consegue validar o token.
   *
   * Ligado é o certo. Desligado existe para o dia em que o provedor sair do ar
   * e a alternativa seja não receber lead nenhum — a decisão é da EGBA, e fica
   * registrada na auditoria da configuração.
   */
  bloquearSemToken: boolean
}

export const CAPTCHA_DESLIGADO: ConfigCaptcha = { provedor: 'nenhum', bloquearSemToken: true }

/** O que o navegador precisa saber. A chave secreta nunca sai do servidor. */
export function captchaPublico(c: ConfigCaptcha | null | undefined): ConfigCaptcha {
  if (!c || c.provedor === 'nenhum' || !c.siteKey?.trim()) return CAPTCHA_DESLIGADO
  return { provedor: c.provedor, siteKey: c.siteKey.trim(), bloquearSemToken: c.bloquearSemToken }
}

export const VERIFICACAO_CAPTCHA: Record<Exclude<ProvedorCaptcha, 'nenhum'>, string> = {
  turnstile: 'https://challenges.cloudflare.com/turnstile/v0/siteverify',
  recaptcha_v3: 'https://www.google.com/recaptcha/api/siteverify',
}
